import type { ExpertSummary } from "../../api";
import { ExpertMark } from "../design/ExpertMark";
import { Icon } from "../design/Icon";
import { SkeletonLines } from "./SkeletonLines";

const STAGES = [
  "queued",
  "transcribing",
  "drafting",
  "writing",
  "done",
] as const;
export type FinalizeStage = (typeof STAGES)[number];

const STAGE_LABEL: Record<FinalizeStage, string> = {
  queued: "Queued",
  transcribing: "Reading the transcript",
  drafting: "Drafting findings",
  writing: "Writing artifacts",
  done: "Done",
};

const KIND_GLYPH: Record<string, { letter: string; color: string }> = {
  md: { letter: "M", color: "spec" },
  csv: { letter: "C", color: "ctd" },
  mermaid: { letter: "G", color: "sys" },
  diff: { letter: "Δ", color: "aws" },
  json: { letter: "J", color: "gdpr" },
};

/** Per-expert finalize progress card. Matches
 *  design-handoff/project/screens-meeting.jsx:FinalizeRow.
 */
export function FinalizeRow({
  expert,
  progress,
  stage,
  archetypeDisplayName,
}: {
  expert: ExpertSummary;
  /** 0..1 overall progress for this expert. */
  progress: number;
  stage: FinalizeStage;
  /** Archetype display name, when known. Renders as a subtitle under
   *  the expert name so post-finalize meetings are comparable by
   *  archetype. Optional for back-compat with legacy meetings. */
  archetypeDisplayName?: string;
}) {
  const c = expert.color_token;
  const stageLabel = STAGE_LABEL[stage];
  const artifacts = expert.finalize_artifacts;
  const pct = Math.floor(Math.min(progress, 1) * 100);
  const done = progress >= 1;
  return (
    <div
      style={{
        padding: 16,
        borderRadius: 10,
        background: "var(--card)",
        border: `1px solid color-mix(in oklab, var(--${c}) 22%, var(--rule))`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <ExpertMark expert={expert} size={36} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <strong
              className="serif"
              style={{ fontSize: 18, fontWeight: 400 }}
            >
              {expert.display_name}
            </strong>
            <span className="mono dim" style={{ fontSize: 11 }}>
              [{expert.short_name}]
            </span>
          </div>
          <div className="dim" style={{ fontSize: 12, marginTop: 2 }}>
            {archetypeDisplayName ? `${archetypeDisplayName} · ` : ""}
            {stageLabel} · {artifacts.length} artifact
            {artifacts.length === 1 ? "" : "s"} queued
          </div>
        </div>
        <span
          className="mono"
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: done ? "var(--ok)" : `var(--${c}-deep)`,
          }}
        >
          {pct}%
        </span>
      </div>

      <div
        style={{
          marginTop: 12,
          height: 4,
          background: "var(--paper-3)",
          borderRadius: 999,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${Math.min(progress, 1) * 100}%`,
            background: `var(--${c})`,
            transition: "width .3s ease-out",
          }}
        />
      </div>

      {artifacts.length > 0 && (
        <div
          style={{
            marginTop: 12,
            display: "grid",
            gridTemplateColumns: `repeat(${Math.min(artifacts.length, 4)}, 1fr)`,
            gap: 8,
          }}
        >
          {artifacts.map((a, i) => {
            const itemP = Math.max(
              0,
              Math.min(1, progress * artifacts.length - i),
            );
            const kind = KIND_GLYPH[a.format] ?? KIND_GLYPH.md;
            return (
              <div
                key={a.filename}
                style={{
                  padding: 10,
                  borderRadius: 6,
                  background: "var(--paper-2)",
                  border: "1px solid var(--rule)",
                  opacity: itemP > 0 ? 1 : 0.5,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 6,
                  }}
                >
                  <KindBadge kind={kind} />
                  <span
                    className="mono"
                    style={{ fontSize: 11.5, fontWeight: 500 }}
                  >
                    {a.filename}
                  </span>
                  {itemP >= 1 && (
                    <Icon name="check" size={11} color="var(--ok)" />
                  )}
                </div>
                <SkeletonLines count={3} progress={itemP} color={c} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function KindBadge({
  kind,
}: {
  kind: { letter: string; color: string };
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 18,
        height: 18,
        borderRadius: 3,
        background: `var(--${kind.color}-tint)`,
        color: `var(--${kind.color}-deep)`,
        fontFamily: "var(--mono)",
        fontSize: 10,
        fontWeight: 700,
        border: `1px solid color-mix(in oklab, var(--${kind.color}) 30%, transparent)`,
      }}
    >
      {kind.letter}
    </span>
  );
}
